import React, { createContext, useContext, useState, useLayoutEffect, ReactNode, useMemo } from "react";

interface TableOverflowContextType {
  isOverflowing: boolean;
}

const TableOverflowContext = createContext<TableOverflowContextType>({
  isOverflowing: false
});

export const useTableOverflow = () => useContext(TableOverflowContext);

interface TableOverflowProviderProps {
  children: ReactNode;
  tableRef: React.RefObject<HTMLElement | null>;
  dependencies?: unknown[];
}

export const TableOverflowProvider = ({ children, tableRef, dependencies = [] }: TableOverflowProviderProps) => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (tableRef.current) {
        const { scrollWidth, clientWidth } = tableRef.current;
        setIsOverflowing(scrollWidth > clientWidth);
      }
    };

    const element = tableRef.current;
    const observer = new ResizeObserver(checkOverflow);

    if (element) {
      observer.observe(element);
      checkOverflow(); // Initial check
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [tableRef, ...dependencies]);

  const value = useMemo(() => ({ isOverflowing }), [isOverflowing]);

  return <TableOverflowContext.Provider value={value}>{children}</TableOverflowContext.Provider>;
};
